import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import getColorCode from 'src/utils/GetThemeColor';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import { getVehicleTrackingsForVehicleDetail } from 'src/redux/Actions/VehicleTrackingOutward';
import {
  getPlants,
  getVehicleTrackingWorkflows,
  getWorkflowStagesForWorkflow,
} from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as ArrowLeftButton } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRightButton } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as FilterIcon } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import truckIcon from 'src/Images/icons/truck/truck-xs.png';
import { formatFieldValue } from 'src/components/Vehicle/Detail/utils';

const vehicleTrackingLogColumns = [
  'Vehicle Tracking Code',
  'Workflow',
  'Plant',
  'Document Numbers',
  'Created',
];

const vehicleTrackingLogPaginationBatchSize = 10;
const vehicleTrackingStatusMap = {
  scheduled: 1,
  inProgress: 2,
  completed: 3,
};
const defaultVehicleTrackingLogFilters = {
  searchTerm: '',
  workflowId: null,
  workflowStageId: null,
  plantId: null,
  startDate: '',
  endDate: '',
};

function VehicleTrackingLogTable({ rows }) {
  const navigate = useNavigate();

  return (
    <div className="vehicle-tracking-log-table-wrap br-top-4">
      <table className="vehicle-detail-table">
        <thead>
          <tr className="vehicle-detail-table-header text-sm-outfit secondary-text-grey">
            {vehicleTrackingLogColumns.map((column) => (
              <th key={column} className="text-nowrap">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <tr
                key={row.vehicle_tracking_number}
                className="vehicle-detail-table-row vehicle-tracking-log-row text-sm-outfit text-medium-bold"
              >
                <td>
                  <div className="div-flex-row cg-10 div-align-center">
                    <img
                      src={truckIcon}
                      alt=""
                      className="vehicle-tracking-log-truck"
                    />
                    {row.workflow_stage_id ? (
                      <button
                        type="button"
                        className="vehicle-master-link-button text-sm-outfit text-medium-bold"
                        onClick={() =>
                          navigate(
                            `/${row.workflow_stage_id}/${row.vehicle_tracking_number}`,
                          )
                        }
                      >
                        {row.vehicle_tracking_code || '-/-'}
                      </button>
                    ) : (
                      <span>{row.vehicle_tracking_code || '-/-'}</span>
                    )}
                  </div>
                </td>
                <td>{formatFieldValue(row.workflow)}</td>
                <td>{formatFieldValue(row.plant)}</td>
                <td>
                  {row.document_numbers?.length
                    ? row.document_numbers.join(', ')
                    : '-/-'}
                </td>
                <td className="text-nowrap">{formatFieldValue(row.created)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={vehicleTrackingLogColumns.length}>
                <div className="div-flex-row div-flex-center vertical-padding-10">
                  <span className="text-sm-outfit text-medium-bold">
                    No vehicle tracking logs found.
                  </span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function VehicleTrackingLogTab({ vehicle }) {
  const dispatch = useDispatch();
  const themeColor = useSelector(
    (state) => state.AuthReducer?.userData?.tenant?.theme,
  );
  const tenantOrganisationId = useSelector(
    (state) => state.AuthReducer?.userData?.organisations?.id,
  );
  const vehicleTrackingsForVehicleDetail = useSelector(
    (state) =>
      state.VehicleTrackingOutwardReducer?.vehicleTrackingsForVehicleDetail,
  );
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { workflows, workflowStagesForWorkflow, plantsData } =
    vehicleTrackingDataReducer;
  const [activeVehicleTrackingState, setActiveVehicleTrackingState] = useState(
    vehicleTrackingStatusMap.scheduled,
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filterFormData, setFilterFormData] = useState(
    defaultVehicleTrackingLogFilters,
  );
  const [appliedFilterFormData, setAppliedFilterFormData] = useState(
    defaultVehicleTrackingLogFilters,
  );
  const vehicleTrackingLogData =
    vehicleTrackingsForVehicleDetail?.[activeVehicleTrackingState] || {};
  const rows = vehicleTrackingLogData?.vehicleTracking || [];
  const totalPages = vehicleTrackingLogData?.totalPages || 1;
  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;
  const workflowOptions =
    workflows?.map((workflowObj) => ({
      value: workflowObj.id,
      label: workflowObj.name,
    })) || [];
  const stageOptions =
    workflowStagesForWorkflow?.map((workflowStageObj) => ({
      value: workflowStageObj.id,
      label: workflowStageObj.name,
    })) || [];
  const plantOptions =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  useEffect(() => {
    if (tenantOrganisationId && vehicle?.id) {
      const appliedFilters = {
        search_term: appliedFilterFormData.searchTerm,
        workflow_id: appliedFilterFormData.workflowId?.value,
        workflow_stage_id: appliedFilterFormData.workflowStageId?.value,
        plant_id: appliedFilterFormData.plantId?.value,
        start_date: appliedFilterFormData.startDate,
        end_date: appliedFilterFormData.endDate,
      };

      dispatch(
        getVehicleTrackingsForVehicleDetail(
          tenantOrganisationId,
          vehicle.id,
          activeVehicleTrackingState,
          pageNumber,
          vehicleTrackingLogPaginationBatchSize,
          appliedFilters,
        ),
      );
    }
  }, [
    dispatch,
    tenantOrganisationId,
    vehicle?.id,
    activeVehicleTrackingState,
    pageNumber,
    appliedFilterFormData,
  ]);

  const handleVehicleTrackingStateChange = (status) => {
    setActiveVehicleTrackingState(status);
    setFilterFormData(defaultVehicleTrackingLogFilters);
    setAppliedFilterFormData(defaultVehicleTrackingLogFilters);
    setPageNumber(1);
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePageNumberChange = (event) => {
    const value = Number(event.target.value);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFilterFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilter = () => {
    setAppliedFilterFormData(filterFormData);
    setPageNumber(1);
    setShowFilters(false);
  };

  const handleResetFilter = () => {
    setFilterFormData(defaultVehicleTrackingLogFilters);
    setAppliedFilterFormData(defaultVehicleTrackingLogFilters);
    setPageNumber(1);
  };

  const handleWorkflowChange = (selectedOption) => {
    setFilterFormData((prev) => ({
      ...prev,
      workflowId: selectedOption,
      workflowStageId: null,
    }));
    if (selectedOption?.value) {
      dispatch(
        getWorkflowStagesForWorkflow(
          tenantOrganisationId,
          selectedOption.value,
        ),
      );
    }
  };

  return (
    <div className="vehicle-tracking-log-tab-content">
      <Row className="m-0">
        <Col
          xxl={showFilters ? 10 : 12}
          xl={showFilters ? 9 : 12}
          lg={showFilters ? 8 : 12}
          md={showFilters ? 8 : 12}
          sm={showFilters ? 0 : 12}
          xs={showFilters ? 0 : 12}
          className={
            showFilters ? 'vehicle-tracking-log-list-with-filter' : 'p-0'
          }
        >
          <div className="div-flex-column rg-10">
            <div className="div-flex-row div-space-between div-align-center">
              <div className="div-flex-row cg-10 padding-top-10">
                <button
                  type="button"
                  className={`vehicle-tracking-log-state-button text-sm-outfit text-medium-bold ${
                    activeVehicleTrackingState ===
                    vehicleTrackingStatusMap.scheduled
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    handleVehicleTrackingStateChange(
                      vehicleTrackingStatusMap.scheduled,
                    )
                  }
                >
                  Scheduled
                </button>
                <button
                  type="button"
                  className={`vehicle-tracking-log-state-button text-sm-outfit text-medium-bold ${
                    activeVehicleTrackingState ===
                    vehicleTrackingStatusMap.inProgress
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    handleVehicleTrackingStateChange(
                      vehicleTrackingStatusMap.inProgress,
                    )
                  }
                >
                  In Progress
                </button>
                <button
                  type="button"
                  className={`vehicle-tracking-log-state-button text-sm-outfit text-medium-bold ${
                    activeVehicleTrackingState ===
                    vehicleTrackingStatusMap.completed
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    handleVehicleTrackingStateChange(
                      vehicleTrackingStatusMap.completed,
                    )
                  }
                >
                  Completed
                </button>
              </div>

              <button
                type="button"
                className="icon-btn-none div-align-center"
                aria-label="Filter Vehicle Tracking logs"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <FilterIcon
                  className="icon-size-xl"
                  color={showFilters ? getColorCode(themeColor) : '#9F9F9F'}
                />
              </button>
            </div>

            <VehicleTrackingLogTable rows={rows} />

            <div className="div-flex-row div-flex-end">
              <div className="div-flex-row cg-20 pagination-container div-align-center">
                <button
                  type="button"
                  className="icon-btn-none"
                  onClick={handlePreviousPage}
                  disabled={pageNumber === 1}
                  aria-label="Previous page"
                >
                  <ArrowLeftButton className="icon-size-sm" />
                </button>
                <input
                  type="number"
                  className="page-number-container input-icon-remove text-sm"
                  value={formattedPageNumber}
                  min={1}
                  max={totalPages}
                  onChange={handlePageNumberChange}
                  aria-label="Enter page number"
                />
                <button
                  type="button"
                  className="icon-btn-none"
                  onClick={handleNextPage}
                  disabled={pageNumber === totalPages}
                  aria-label="Next page"
                >
                  <ArrowRightButton className="icon-size-sm" />
                </button>
                <span className="text-sm-outfit text-medium-bold">
                  Page {pageNumber} of {totalPages}
                </span>
              </div>
            </div>
          </div>
        </Col>

        {showFilters && (
          <Col
            xxl={2}
            xl={3}
            lg={4}
            md={4}
            sm={12}
            xs={12}
            className="border-left border-bottom p-0"
          >
            <div className="filter-list-item border-bottom">
              <span className="text-sm-outfit text-medium-bold">Filter</span>
            </div>
            <div className="div-flex-column div-space-between rg-20">
              <div className="filter-list-item-container-scroll div-flex-column">
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">
                    Search
                  </span>
                  <input
                    name="searchTerm"
                    className="form-input-section"
                    value={filterFormData.searchTerm}
                    onChange={handleFilterInputChange}
                  />
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">
                    Workflow
                  </span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="workflowId"
                      className="multi-select-input-section"
                      value={filterFormData.workflowId || ''}
                      options={workflowOptions}
                      classNamePrefix="react-select"
                      placeholder="Select Workflow"
                      onFocus={() =>
                        dispatch(
                          getVehicleTrackingWorkflows(tenantOrganisationId),
                        )
                      }
                      onChange={handleWorkflowChange}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRightButton
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">Stage</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="workflowStageId"
                      className="multi-select-input-section"
                      value={filterFormData.workflowStageId || ''}
                      options={stageOptions}
                      classNamePrefix="react-select"
                      placeholder="Select Stage"
                      onFocus={() => {
                        if (filterFormData.workflowId?.value) {
                          dispatch(
                            getWorkflowStagesForWorkflow(
                              tenantOrganisationId,
                              filterFormData.workflowId.value,
                            ),
                          );
                        }
                      }}
                      onChange={(selectedOption) =>
                        setFilterFormData((prev) => ({
                          ...prev,
                          workflowStageId: selectedOption,
                        }))
                      }
                    />
                    <span className="form-input-section-icon">
                      <ArrowRightButton
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">Plant</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="plantId"
                      className="multi-select-input-section"
                      value={filterFormData.plantId || ''}
                      options={plantOptions}
                      classNamePrefix="react-select"
                      placeholder="Select Plant"
                      onFocus={() => dispatch(getPlants(tenantOrganisationId))}
                      onChange={(selectedOption) =>
                        setFilterFormData((prev) => ({
                          ...prev,
                          plantId: selectedOption,
                        }))
                      }
                    />
                    <span className="form-input-section-icon">
                      <ArrowRightButton
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">
                    Start Date
                  </span>
                  <div className="input-wrapper">
                    <input
                      name="startDate"
                      className="form-input-section input-icon-date"
                      type="date"
                      value={filterFormData.startDate}
                      onChange={handleFilterInputChange}
                    />
                    <span className="form-input-section-icon">
                      <Calendar className="icon-size-sm" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm-outfit text-medium-bold">
                    End Date
                  </span>
                  <div className="input-wrapper">
                    <input
                      name="endDate"
                      className="form-input-section input-icon-date"
                      type="date"
                      value={filterFormData.endDate}
                      onChange={handleFilterInputChange}
                    />
                    <span className="form-input-section-icon">
                      <Calendar className="icon-size-sm" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                <button
                  type="button"
                  className={`common-btn-outlined border-${themeColor}`}
                  onClick={handleResetFilter}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className={`common-btn theme-background-${themeColor}`}
                  onClick={handleApplyFilter}
                >
                  Apply
                </button>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}
